import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Download, Loader2, RefreshCw, Search, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const mappingSlots = [0, 1, 2];

const cleanMappings = (mappings) =>
  Object.fromEntries(
    Object.entries(mappings)
      .map(([target, sources]) => [target, (sources || []).filter(Boolean)])
      .filter(([, sources]) => sources.length > 0)
  );

const defaultTargetFields = [
  { key: "sku", label: "SKU (required for match)" },
  { key: "name", label: "Product Name" },
  { key: "description", label: "Description" },
  { key: "collection_name", label: "Collection" },
  { key: "category", label: "Category" },
  { key: "colour", label: "Colour" },
  { key: "price_numeric", label: "Price Numeric" },
  { key: "assembled_weight_kg", label: "Weight (kg)" },
  { key: "stock_quantity", label: "Stock Quantity" },
  { key: "ean_code", label: "EAN" },
  { key: "images", label: "Images" },
];

export const FTGManualTab = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadJob, setUploadJob] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [mappingSearch, setMappingSearch] = useState("");
  const [mappings, setMappings] = useState({});

  const [updateExisting, setUpdateExisting] = useState(true);
  const [createNew, setCreateNew] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [allPreviewRowIds, setAllPreviewRowIds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [rowSearch, setRowSearch] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [approve, setApprove] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const filteredTargetFields = useMemo(() => {
    const term = mappingSearch.trim().toLowerCase();
    if (!term) return defaultTargetFields;
    return defaultTargetFields.filter((field) => `${field.key} ${field.label}`.toLowerCase().includes(term));
  }, [mappingSearch]);

  const filteredPreviewRows = useMemo(() => {
    const term = rowSearch.trim().toLowerCase();
    if (!term) return previewRows;
    return previewRows.filter((row) =>
      `${row.sku || ""} ${row.reason || ""} ${row.existing_product_name || ""}`.toLowerCase().includes(term)
    );
  }, [previewRows, rowSearch]);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/import-manager/ftg/logs`, { params: { limit: 20 } });
      setLogs(res.data.logs || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load FTG logs");
    }
    setLogsLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleUploadCsv = async () => {
    if (!csvFile) {
      toast.error("Please choose a CSV file");
      return;
    }

    setUploading(true);
    setApplyResult(null);
    setPreviewSummary(null);
    setPreviewRows([]);
    setAllPreviewRowIds([]);
    setSelectedRowIds([]);

    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      const res = await axios.post(`${API}/admin/import-manager/ftg/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadJob(res.data);
      setCsvHeaders(res.data.headers || []);

      const initialMappings = {};
      defaultTargetFields.forEach((field) => {
        const defaults = (res.data.default_mappings || {})[field.key] || [];
        initialMappings[field.key] = [defaults[0] || "", defaults[1] || "", defaults[2] || ""];
      });
      setMappings(initialMappings);

      toast.success(`CSV uploaded with ${res.data.total_rows} rows`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
    }

    setUploading(false);
  };

  const updateMappingSlot = (targetField, slot, header) => {
    setMappings((prev) => {
      const next = prev[targetField] ? [...prev[targetField]] : ["", "", ""];
      next[slot] = header;
      return { ...prev, [targetField]: next };
    });
  };

  const runPreview = async () => {
    if (!uploadJob?.job_id) {
      toast.error("Upload CSV first");
      return;
    }
    if (!updateExisting && !createNew) {
      toast.error("Enable at least one mode: Update or Create");
      return;
    }

    setPreviewLoading(true);
    try {
      const cleaned = cleanMappings(mappings);
      const previewRes = await axios.post(`${API}/admin/import-manager/ftg/preview`, {
        job_id: uploadJob.job_id,
        mappings: cleaned,
        update_existing: updateExisting,
        create_new: createNew,
      });

      setPreviewSummary(previewRes.data.summary);
      setPage(1);
      await fetchPreviewRows(uploadJob.job_id, 1, pageSize);
      toast.success("FTG manual preview generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate preview");
    }
    setPreviewLoading(false);
  };

  const fetchPreviewRows = async (jobId, nextPage = 1, nextPageSize = pageSize) => {
    const res = await axios.get(`${API}/admin/import-manager/ftg/preview/${jobId}`, {
      params: { page: nextPage, page_size: nextPageSize },
    });

    setPreviewRows(res.data.rows || []);
    setAllPreviewRowIds((res.data.all_row_ids || []).filter(Boolean));
    setPreviewSummary(res.data.summary || null);
    setTotalPages(res.data.pagination?.total_pages || 1);
    setTotalRows(res.data.pagination?.total_rows || 0);
    setPage(res.data.pagination?.page || 1);
    setPageSize(res.data.pagination?.page_size || nextPageSize);

    const allRowIds = (res.data.all_row_ids || []).filter(Boolean);
    setSelectedRowIds((prev) => {
      if (prev.length === 0) {
        return allRowIds;
      }
      const rowIds = (res.data.rows || []).map((row) => row.row_id);
      return Array.from(new Set([...prev, ...rowIds]));
    });
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRowIds((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  const toggleVisibleRows = () => {
    const visibleIds = filteredPreviewRows.map((row) => row.row_id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedRowIds.includes(id));
    if (allSelected) {
      setSelectedRowIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedRowIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const applyChanges = async () => {
    if (!uploadJob?.job_id) {
      toast.error("No FTG job selected");
      return;
    }
    if (!approve) {
      toast.error("Please approve before applying changes");
      return;
    }
    if (selectedRowIds.length === 0) {
      toast.error("Please select at least one row");
      return;
    }

    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/import-manager/ftg/apply`, {
        job_id: uploadJob.job_id,
        selected_row_ids: selectedRowIds,
        approve: true,
        update_existing: updateExisting,
        create_new: createNew,
      });
      setApplyResult(res.data);
      fetchLogs();
      toast.success("FTG manual import applied");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply FTG manual import");
    }
    setApplying(false);
  };

  const downloadFailedRowsCsv = () => {
    const csv = applyResult?.failed_rows_csv;
    if (!csv) {
      toast.info("No failed rows CSV available");
      return;
    }
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `ftg_manual_failed_rows_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="import-manager-ftg-manual-tab-content">
      <div className="bg-white rounded-lg shadow-warm p-6" data-testid="ftg-manual-upload-card">
        <h2 className="font-medium text-lg mb-2">FTG Manual CSV Upload</h2>
        <p className="text-sm text-gray-600 mb-4">Upload any CSV file, map columns to product DB fields, preview rows, and approve selected changes.</p>

        <div className="flex flex-col xl:flex-row gap-3 xl:items-end">
          <div className="flex-1">
            <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} data-testid="ftg-manual-csv-input" />
          </div>
          <Button onClick={handleUploadCsv} disabled={uploading || !csvFile} data-testid="ftg-manual-upload-button">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload CSV
          </Button>
        </div>

        {uploadJob && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="ftg-manual-upload-summary">
            <div>File: <strong data-testid="ftg-manual-file-name">{uploadJob.file_name}</strong></div>
            <div>Rows: <strong data-testid="ftg-manual-total-rows">{uploadJob.total_rows}</strong></div>
            <div>Headers: <strong data-testid="ftg-manual-total-headers">{csvHeaders.length}</strong></div>
            <div>Job: <strong data-testid="ftg-manual-job-id">{uploadJob.job_id}</strong></div>
          </div>
        )}
      </div>

      {uploadJob && (
        <div className="bg-white rounded-lg shadow-warm p-6" data-testid="ftg-manual-mapping-card">
          <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
            <h2 className="font-medium text-lg">CSV → Product DB Mapping</h2>
            <Input value={mappingSearch} onChange={(e) => setMappingSearch(e.target.value)} placeholder="Search target field" className="max-w-[280px]" data-testid="ftg-manual-mapping-search-input" />
          </div>

          <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm border rounded px-3 py-2">
              <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="ftg-manual-toggle-update-existing" />
              Update existing products
            </label>
            <label className="flex items-center gap-2 text-sm border rounded px-3 py-2">
              <input type="checkbox" checked={createNew} onChange={(e) => setCreateNew(e.target.checked)} data-testid="ftg-manual-toggle-create-new" />
              Create new products (SKU required, defaults auto-fill)
            </label>
          </div>

          <div className="overflow-auto border rounded max-h-[420px]" data-testid="ftg-manual-mapping-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  <th className="text-left p-2">DB Target</th>
                  <th className="text-left p-2">Priority 1</th>
                  <th className="text-left p-2">Priority 2</th>
                  <th className="text-left p-2">Priority 3</th>
                </tr>
              </thead>
              <tbody>
                {filteredTargetFields.map((field) => {
                  const values = mappings[field.key] || ["", "", ""];
                  return (
                    <tr key={field.key} className="border-t" data-testid={`ftg-manual-mapping-row-${field.key}`}>
                      <td className="p-2 font-medium">{field.label}</td>
                      {mappingSlots.map((slot) => (
                        <td key={`${field.key}-${slot}`} className="p-2">
                          <select
                            className="border rounded px-2 py-1.5 w-full"
                            value={values[slot] || ""}
                            onChange={(e) => updateMappingSlot(field.key, slot, e.target.value)}
                            data-testid={`ftg-manual-mapping-select-${field.key}-priority-${slot + 1}`}
                          >
                            <option value="">None</option>
                            {csvHeaders.map((header) => (
                              <option key={`${field.key}-${slot}-${header}`} value={header}>{header}</option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <Button onClick={runPreview} disabled={previewLoading} data-testid="ftg-manual-run-preview-button">
              {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Preview Mapped Changes
            </Button>
          </div>
        </div>
      )}

      {previewSummary && (
        <div className="bg-white rounded-lg shadow-warm p-6" data-testid="ftg-manual-preview-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4" data-testid="ftg-manual-preview-summary-grid">
            <div>Total: <strong data-testid="ftg-manual-preview-total-rows">{previewSummary.total_rows}</strong></div>
            <div>Update: <strong data-testid="ftg-manual-preview-update-count">{previewSummary.update_count}</strong></div>
            <div>Create: <strong data-testid="ftg-manual-preview-create-count">{previewSummary.create_count}</strong></div>
            <div>Skip: <strong data-testid="ftg-manual-preview-skip-count">{previewSummary.skip_count}</strong></div>
          </div>

          <div className="flex flex-col xl:flex-row gap-3 justify-between mb-3">
            <div className="flex gap-2 items-center">
              <Input value={rowSearch} onChange={(e) => setRowSearch(e.target.value)} placeholder="Search preview rows" className="max-w-[280px]" data-testid="ftg-manual-preview-row-search-input" />
              <Button variant="outline" onClick={toggleVisibleRows} data-testid="ftg-manual-toggle-visible-rows-button">Toggle Visible</Button>
              <Button variant="outline" onClick={() => setSelectedRowIds(allPreviewRowIds)} data-testid="ftg-manual-select-all-rows-button">Select All</Button>
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-sm text-gray-600">Page Size</label>
              <select
                className="border rounded px-2 py-1.5 text-sm"
                value={pageSize}
                onChange={async (e) => {
                  const next = Number(e.target.value);
                  if (uploadJob?.job_id) {
                    await fetchPreviewRows(uploadJob.job_id, 1, next);
                  }
                }}
                data-testid="ftg-manual-page-size-select"
              >
                {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              <Badge variant="secondary" data-testid="ftg-manual-selected-rows-count">{selectedRowIds.length} selected</Badge>
            </div>
          </div>

          <div className="overflow-auto border rounded max-h-[520px]" data-testid="ftg-manual-preview-table">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  <th className="text-left p-2">Pick</th>
                  <th className="text-left p-2">CSV Row</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-left p-2">SKU</th>
                  <th className="text-left p-2">Reason</th>
                  <th className="text-left p-2">Mapped Values</th>
                </tr>
              </thead>
              <tbody>
                {filteredPreviewRows.map((row) => (
                  <tr key={row.row_id} className="border-t align-top" data-testid={`ftg-manual-preview-row-${row.row_id}`}>
                    <td className="p-2">
                      <input type="checkbox" checked={selectedRowIds.includes(row.row_id)} onChange={() => toggleRowSelection(row.row_id)} data-testid={`ftg-manual-row-checkbox-${row.row_id}`} />
                    </td>
                    <td className="p-2">{row.csv_row_number}</td>
                    <td className="p-2"><Badge>{row.action}</Badge></td>
                    <td className="p-2 font-medium">{row.sku || "-"}</td>
                    <td className="p-2">{row.reason}</td>
                    <td className="p-2">
                      <pre className="text-xs whitespace-pre-wrap break-words bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">{JSON.stringify(row.mapped_values || {}, null, 2)}</pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between" data-testid="ftg-manual-pagination-controls">
            <p className="text-sm text-gray-600">Page {page} of {totalPages} • {totalRows} rows</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => uploadJob?.job_id && fetchPreviewRows(uploadJob.job_id, page - 1, pageSize)} data-testid="ftg-manual-pagination-prev-button">Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => uploadJob?.job_id && fetchPreviewRows(uploadJob.job_id, page + 1, pageSize)} data-testid="ftg-manual-pagination-next-button">Next</Button>
            </div>
          </div>

          <div className="mt-4 p-3 border rounded bg-gray-50" data-testid="ftg-manual-approval-block">
            <label className="flex items-start gap-2 text-sm mb-3">
              <input type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} data-testid="ftg-manual-approve-checkbox" />
              <span>I reviewed the preview rows and approve applying selected changes.</span>
            </label>
            <Button onClick={applyChanges} disabled={applying || !approve || selectedRowIds.length === 0} data-testid="ftg-manual-apply-button">
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Approve & Apply Selected Rows
            </Button>
          </div>
        </div>
      )}

      {applyResult && (
        <div className="bg-white rounded-lg shadow-warm p-6" data-testid="ftg-manual-result-card">
          <h2 className="font-medium text-lg mb-3">Apply Result</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-4">
            <div>Selected: <strong data-testid="ftg-manual-result-total-selected">{applyResult.summary?.total_selected || 0}</strong></div>
            <div>Updated: <strong data-testid="ftg-manual-result-updated">{applyResult.summary?.updated || 0}</strong></div>
            <div>Created: <strong data-testid="ftg-manual-result-created">{applyResult.summary?.inserted || 0}</strong></div>
            <div>Skipped: <strong data-testid="ftg-manual-result-skipped">{applyResult.summary?.skipped || 0}</strong></div>
            <div>Failed: <strong data-testid="ftg-manual-result-failed">{applyResult.summary?.failed || 0}</strong></div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Button variant="outline" onClick={downloadFailedRowsCsv} data-testid="ftg-manual-download-failed-csv-button">
              <Download className="w-4 h-4 mr-2" />
              Download Failed-row CSV
            </Button>
          </div>

          <div className="border rounded max-h-[240px] overflow-y-auto" data-testid="ftg-manual-failed-rows-panel">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="text-left p-2">CSV Row</th>
                  <th className="text-left p-2">SKU</th>
                  <th className="text-left p-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(applyResult.failed_rows || []).map((row, index) => (
                  <tr key={`${row.csv_row_number}-${index}`} className="border-t" data-testid={`ftg-manual-failed-row-${index}`}>
                    <td className="p-2">{row.csv_row_number}</td>
                    <td className="p-2">{row.sku || "-"}</td>
                    <td className="p-2">{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-warm p-6" data-testid="ftg-manual-logs-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium text-lg">FTG Manual Run Logs</h2>
          <Button variant="outline" onClick={fetchLogs} disabled={logsLoading} data-testid="ftg-manual-refresh-logs-button">
            {logsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        <div className="overflow-auto max-h-[300px] border rounded" data-testid="ftg-manual-logs-table">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="text-left p-2">Time</th>
                <th className="text-left p-2">Updated</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Skipped</th>
                <th className="text-left p-2">Failed</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t" data-testid={`ftg-manual-log-row-${log.id}`}>
                  <td className="p-2">{new Date(log.created_at).toLocaleString()}</td>
                  <td className="p-2">{log.summary?.updated || 0}</td>
                  <td className="p-2">{log.summary?.inserted || 0}</td>
                  <td className="p-2">{log.summary?.skipped || 0}</td>
                  <td className="p-2">{log.summary?.failed || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
